import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils/formatters';

const CartItem = ({ item, onUpdateQuantity, onRemove, onMoveToWishlist, onToggleSelect }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const itemImage = item?.metadata?.screenshot || item?.image;

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity > 0) {
      onUpdateQuantity(item?.id, newQuantity);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-3 sm:p-4 shadow-soft hover:shadow-floating transition-smooth">
      <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
        {/* Selection Checkbox */}
        <div className="hidden sm:flex items-center pt-2">
          <input
            type="checkbox"
            checked={item?.selected}
            disabled={item?.stockQuantity <= 0}
            onChange={() => onToggleSelect(item?.cartItemId || item?.id)}
            className={`w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2 flex-shrink-0 ${item?.stockQuantity <= 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          />
        </div>

        {/* Mobile: Checkbox + Image Row */}
        <div className="flex sm:hidden items-center gap-3">
          <input
            type="checkbox"
            checked={item?.selected}
            disabled={item?.stockQuantity <= 0}
            onChange={() => onToggleSelect(item?.cartItemId || item?.id)}
            className={`w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2 flex-shrink-0 ${item?.stockQuantity <= 0 ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          />
          
          {/* Product Image - Mobile */}
          <div className="flex-shrink-0 relative">
            <div className="w-16 h-16 bg-muted rounded-lg overflow-hidden flex items-center justify-center">
              {itemImage ? (
                <Image
                  src={itemImage}
                  alt={item?.imageAlt || `${item?.name} product image`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <img 
                  src="/assets/images/no_image.png" 
                  alt="Default product placeholder icon"
                  className="w-10 h-10 object-contain opacity-50"
                />
              )}
            </div>
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full whitespace-nowrap">
              {item?.isComponent ? 'Component' : 'Product'}
            </div>
          </div>
        </div>

        {/* Product Image - Desktop */}
        <div className="hidden sm:block flex-shrink-0 relative">
          <div className="w-20 h-20 bg-muted rounded-lg overflow-hidden flex items-center justify-center">
            {itemImage ? (
              <Image
                src={itemImage}
                alt={item?.imageAlt || `${item?.name} product image`}
                className="w-full h-full object-cover"
              />
            ) : (
              <img 
                src="/assets/images/no_image.png" 
                alt="Default product placeholder icon"
                className="w-12 h-12 object-contain opacity-50"
              />
            )}
          </div>
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-primary text-primary-foreground text-xs rounded-full whitespace-nowrap">
            {item?.isComponent ? 'Component' : 'Product'}
          </div>
        </div>

        {/* Product Details Column */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground break-words">
                {item?.name}
              </h3>
              <p className="text-xs text-muted-foreground mt-1 break-words">
                by {item?.sellerBusinessName || 'Unknown Seller'}
              </p>

              {/* Manufacturing Options Display */}
              {item?.metadata?.type === 'manufacturing' && (
                <div className="mt-2 flex flex-col gap-1.5 p-2 bg-primary/5 rounded-lg border border-primary/10">
                   <div className="flex items-center gap-1.5">
                      <Icon name="Zap" size={12} className="text-primary fill-current" />
                      <span className="text-[10px] font-black text-primary uppercase tracking-widest">Manufacturing Service</span>
                   </div>
                   <div className="flex flex-wrap gap-x-4 gap-y-1">
                      {item.metadata.mfgType && (
                        <div className="flex items-center gap-1">
                           <span className="text-[10px] font-bold text-muted-foreground uppercase">Type:</span>
                           <span className="text-xs font-black text-foreground">{item.metadata.mfgType}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                         <span className="text-[10px] font-bold text-muted-foreground uppercase">Material:</span>
                         <span className="text-xs font-black text-foreground">{item.metadata.material}</span>
                      </div>
                      {item.metadata.color && (
                        <div className="flex items-center gap-1">
                            <span className="text-[10px] font-bold text-muted-foreground uppercase">Color:</span>
                            <span className="text-xs font-black text-foreground flex items-center gap-1.5">
                              <span>{item.metadata.color}</span>
                              {((item.metadata.isMetallic) || (item.metadata.metallic !== undefined && parseFloat(item.metadata.metallic) > 0)) && (
                                <span className="text-[9px] text-blue-200 bg-blue-900/60 px-1.5 py-0.5 rounded font-black shrink-0" title={`Metallic: ${item.metadata.metallic || 1.0}`}>M</span>
                              )}
                            </span>
                        </div>
                      )}
                   </div>
                </div>
              )}
              
              {/* Stock and Compatibility Status */}
              <div className="flex flex-wrap items-center gap-2 mt-2">
                {item?.stockQuantity <= 0 ? (
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1 bg-error/10 px-2 py-0.5 rounded w-fit">
                      <Icon name="AlertCircle" size={14} className="text-error flex-shrink-0" />
                      <span className="text-xs font-bold text-error whitespace-nowrap uppercase">Out of Stock</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground italic">
                      Leave in cart or Save for Later to get notified when back in stock.
                    </p>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-success font-medium">In Stock</span>
                    {item?.vinSn && (
                      <span className="text-xs text-gray-500 font-mono px-2 py-0.5 bg-gray-100 rounded-sm">VIN: {item.vinSn}</span>
                    )}
                  </div>
                )}
                
                {item?.compatibility === 'compatible' && (
                  <div className="flex items-center gap-1">
                    <Icon name="CheckCircle" size={14} className="text-success flex-shrink-0" />
                    <span className="text-xs text-success whitespace-nowrap">Compatible</span>
                  </div>
                )}
                {item?.compatibility === 'warning' && (
                  <div className="flex items-center gap-1">
                    <Icon name="AlertTriangle" size={14} className="text-warning flex-shrink-0" />
                    <span className="text-xs text-warning whitespace-nowrap">Check compatibility</span>
                  </div>
                )}
                {item?.compatibility === 'incompatible' && (
                  <div className="flex items-center gap-1">
                    <Icon name="XCircle" size={14} className="text-error flex-shrink-0" />
                    <span className="text-xs text-error whitespace-nowrap">Incompatible</span>
                  </div>
                )}
              </div>

              {/* File Info */}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                <span className="whitespace-nowrap">{item?.fileSize}</span>
                <span className="hidden sm:inline">•</span>
                <span className="whitespace-nowrap">{item?.format}</span>
                <span className="hidden sm:inline">•</span>
                <span className="whitespace-nowrap">Est. {item?.downloadTime}</span>
              </div>
            </div>

            {/* Price and Quantity Controls */}
            <div className="flex flex-row sm:flex-col items-center sm:items-end justify-between sm:justify-start gap-3 sm:gap-2">
              <div className="text-left sm:text-right">
                {item.bundleId ? (
                    // BUNDLED ITEM: Show Discounted (Cart) Price as Primary
                    <>
                        <div className="text-base sm:text-lg font-bold text-foreground whitespace-nowrap">
                          {formatPrice(item?.cartPrice)}
                        </div>
                        {Number(item.price) > Number(item.cartPrice) && (
                          <div className="text-xs text-muted-foreground line-through whitespace-nowrap">
                            {formatPrice(item?.price)}
                          </div>
                        )}
                        <span className="text-[10px] text-success font-medium whitespace-nowrap bg-success/10 px-1.5 py-0.5 rounded mt-1 inline-block">
                            {Number(item.price) > Number(item.cartPrice) ? 'Bundle Discount' : 'Bundled Package'}
                        </span>
                    </>
                ) : (
                    // REGULAR ITEM
                    <>
                        <div className="text-base sm:text-lg font-bold text-foreground whitespace-nowrap">
                          {formatPrice(item?.price)}
                        </div>
                        {item.cartPrice !== undefined && item.cartPrice !== null && Number(item.price) !== Number(item.cartPrice) && (
                          <div className={`text-xs whitespace-nowrap flex items-center justify-end gap-1 ${Number(item.price) > Number(item.cartPrice) ? 'text-red-500' : 'text-green-600'}`}>
                            {Number(item.price) > Number(item.cartPrice) ? (
                                <Icon name="TrendingUp" size={12} className="text-red-500" />
                            ) : (
                                <Icon name="TrendingDown" size={12} className="text-green-600" />
                            )}
                            Cart price: {formatPrice(item?.cartPrice)}
                          </div>
                        )}
                    </>
                )}
              </div>

              {/* Quantity Controls */}
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => handleQuantityChange(item?.quantity - 1)}
                    disabled={item?.quantity <= 1 || !!item?.vinSn}
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                    title="Decrease quantity"
                  >
                    <Icon name="Minus" size={12} />
                  </Button>
                  <span className="text-sm font-medium min-w-[1.5rem] text-center flex-shrink-0">
                    {item?.quantity}
                  </span>
                  <Button
                    variant="outline"
                    size="xs"
                    onClick={() => {
                      if ((item?.purchaseLimit && item?.quantity >= item.purchaseLimit)) {
                        alert(`You have reached the maximum purchase limit (${item.purchaseLimit}) for this item.`);
                        return;
                      }
                      if ((item?.stockQuantity && item?.quantity >= item.stockQuantity)) {
                        alert(`Only ${item.stockQuantity} units of this item are available in stock.`);
                        return;
                      }
                      handleQuantityChange(item?.quantity + 1);
                    }}
                    disabled={!!item?.vinSn}
                    className="h-7 w-7 sm:h-8 sm:w-8 p-0 flex-shrink-0"
                    title={item.bundleId ? "Quantity managed by bundle" : "Increase quantity"}
                  >
                    <Icon name="Plus" size={12} />
                  </Button>
                </div>
                {item?.vinSn && <p className="text-[10px] text-gray-400 italic text-right mt-0.5">Qty locked (Unique VIN)</p>}
                
                {item?.purchaseLimit && !item.bundleId && (
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    Limit: {item.purchaseLimit}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Expanded Details */}
          {isExpanded && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground leading-relaxed break-words">
                {item?.description || "No description provided for this item."}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between mt-3 sm:mt-4 gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="ghost"
                size="xs"
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex-shrink-0 h-8"
              >
                <Icon name={isExpanded ? "ChevronUp" : "ChevronDown"} size={14} />
                <span className="ml-1 text-xs">{isExpanded ? 'Less' : 'Details'}</span>
              </Button>
              
              <Button
                variant="ghost"
                size="xs"
                onClick={() => onMoveToWishlist(item?.id)}
                className="flex-shrink-0 h-8"
              >
                <Icon name="Heart" size={14} />
                <span className="ml-1 text-xs whitespace-nowrap">Save for Later</span>
              </Button>
            </div>

            <Button
              variant="ghost"
              size="xs"
              onClick={() => onRemove(item?.id)}
              className="text-error hover:text-error hover:bg-error/10 flex-shrink-0 h-8 sm:ml-auto"
            >
              <Icon name="Trash2" size={14} />
              <span className="ml-1 text-xs">Remove</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartItem;
